import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@src/redux/hooks.ts';
import styles from './FJVideo.module.less';
import FJVideo from '@src/components/FJModals/FJAdjustDurationModal/video/FJVideo.tsx';
import FJTrimAction from '@src/redux/action/FJTrimAction.ts';
import FJMathUtil from '@src/util/FJMathUtil';

const TRIM_VIDEO_BOX_HEIGHT = 500;

/** 与 `.box` 内 contain 缩放一致，供加载前固定 video / loadingOverlay 比例 */
function getTrimVideoDisplaySize(ratio: number, boxW: number, boxH: number) {
    const r = ratio > 0 ? ratio : 9 / 16;
    const containerR = boxW / boxH;
    if (r >= containerR) {
        return { width: boxW, height: boxW / r };
    }
    return { width: boxH * r, height: boxH };
}

const FJVideoContainer = () => {
    const dispatch = useAppDispatch();

    const { ratio, duration, currentTime, isPlaying, startTime, endTime, isShow } = useAppSelector(state => ({
        ratio: state.modal.cut.ratio,
        duration: state.modal.cut.totalTime,
        currentTime: state.modal.cut.currentTime,
        isPlaying: state.modal.cut.isPlaying,
        startTime: state.modal.cut.startTime,
        endTime: state.modal.cut.endTime,
        isShow: state.modal.cut.isShow,
        thumbnailImageUrl: state.modal.cut.thumbnailImageUrl,
    }));

    const handlePlay = useCallback(() => {
        dispatch(FJTrimAction.playAction());
    }, [dispatch]);

    const handlePause = useCallback(() => {
        dispatch(FJTrimAction.pauseAction());
    }, [dispatch]);

    useEffect(() => {
        if (!isShow) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== ' ' && e.code !== 'Space') return;
            const t = e.target as HTMLElement | null;
            if (t?.closest('textarea, select, [contenteditable="true"], input:not([type="range"])')) return;
            e.preventDefault();
            if (isPlaying) handlePause();
            else handlePlay();
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [isShow, isPlaying, handlePlay, handlePause]);

    const onSeek = (time: number) => {
        dispatch(FJTrimAction.seekAction(time + startTime));
    };

    const onLoadedCallback = () => {
        dispatch(FJTrimAction.seekAction(startTime));
    };

    const boxWidth = ratio >= 1 ? 500 : 278;
    const videoDisplaySize = useMemo(
        () => getTrimVideoDisplaySize(ratio, boxWidth, TRIM_VIDEO_BOX_HEIGHT),
        [ratio, boxWidth],
    );

    const activeDuration = FJMathUtil.getValueInRange(endTime - startTime, 0, duration);
    // currentTime为完整的视频时长的当前时间,需要转换成裁剪后的时间段上的时间
    let currentVideoTime = currentTime - startTime;
    if (currentTime <= startTime) {
        currentVideoTime = 0;
    } else if (currentTime > endTime) {
        currentVideoTime = endTime - startTime;
    }

    return (
        <div className={styles.box} style={{ width: boxWidth }}>
            {/* <div
                className={styles.thumbnail_blur_bg}
                style={{ backgroundImage: `url(${thumbnailImageUrl.trim()})` }}
                aria-hidden
            /> */}
            {/* 前景居中视频 */}
            <FJVideo
                displayWidth={videoDisplaySize.width}
                displayHeight={videoDisplaySize.height}
                duration={activeDuration}
                currentTime={currentVideoTime}
                isPlaying={isPlaying}
                onSeek={onSeek}
                onPlay={handlePlay}
                onPause={handlePause}
                onLoadedCallback={onLoadedCallback}
            />
        </div>
    );
};

export default React.memo(FJVideoContainer);
